<template lang="pug">
el-form(
  ref="refForm"
  :rules="rules"
  :model="item"
  status-icon
  label-position="top"
).c-dictionary-detail-form
  app-main-content-layout(title='Dictionary' back-to='/manage-content/directory/dictionary')
    template(#actions)
      el-button(type="danger" plain @click="removeDictionaryItem").has-fai.min-w-110
        fai(type="fal" icon="trash").size-18
        span Delete

      el-button(type="primary" @click="validateForm" v-loading="loading").has-fai.min-w-110
        fai(type="fal" icon="save").size-18
        span Save

    template(#col-left)
      .c-content-section
        el-form-item(label="Term" prop="term")
          el-input(v-model="item.term")

        el-form-item(label="Bullhorns", prop="bullhornId")
          el-select(v-model="item.bullhornIds" placeholder="Select" multiple).w-100
            el-option(
              v-for="lookups in bullhorns.bullhorns"
              :key="lookups.id"
              :label="lookups.bullhorn"
              :value="lookups.id"
            )

        el-form-item(label="Description" prop="description")
          el-input(v-model="item.description" type="textarea" :rows="6")

        el-form-item(label="Source" prop="source").mb-0
          el-input(v-model="item.source")

    template(#col-right)
      .c-content-section
        el-form-item(label="Creator")
          el-input(v-model="item.creator" disabled)

        el-form-item
          template(#label)
            | Post Date
            span.text-muted.ms-2 Active for 3 Days
          el-input(v-model="item.postDate" disabled)

        el-form-item(label="Post Time").mb-0
          el-input(v-model="item.postTime" disabled)
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-dictionary-detail-form {
  padding: 2rem;
  
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &:focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Select Styling
:deep(.el-select) {
  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    min-height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }
  }

  .el-select__tags {
    max-width: 100%;
  }

  .el-tag {
    background-color: rgba(67, 73, 221, 0.1);
    border-color: rgba(67, 73, 221, 0.2);
    color: $color-purple-text;
    border-radius: 6px;
    padding: 0 8px;
    height: 24px;
    line-height: 22px;
    font-size: 0.75rem;
    font-weight: 500;
  }
}

:deep(.el-select-dropdown) {
  border-radius: 12px;
  border: 1.5px solid $gray-200;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  margin-top: 6px;

  .el-select-dropdown__item {
    border-radius: 8px;
    padding: 10px 12px;
    margin: 2px 6px;
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    &.hover {
      background-color: rgba(67, 73, 221, 0.08);
      color: $color-purple-text;
    }

    &.selected {
      background-color: rgba(67, 73, 221, 0.12);
      color: $color-purple-text;
      font-weight: 600;
    }
  }
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Text Muted
.text-muted {
  color: $gray-500;
  font-size: 0.75rem;
  font-weight: 400;
}

// Responsive Design
@media (max-width: 768px) {
  .c-dictionary-detail-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .c-dictionary-detail-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>

<script lang="ts">
import {
  defineComponent,
  reactive,
  ref,
  onMounted,
} from 'vue';

import { requiredRule } from '@/data/rules';
import shared from './shared';

export default defineComponent({
  name: 'ManageContent_Directory_DictionaryDeail',
  setup() {
    const {
      route,
      router,
      loading,
      getBullhorns,
      getDictionaryItem,
      saveDictionaryItem,
      removeItemGetConfirm,
      dateAndTime,
    } = shared();

    const item = reactive({
      id: -1,
      term: '',
      description: '',
      bullhornIds: '',
      source: '',
      creator: '',
      postDate: '',
      postTime: '',
    });

    const refForm = ref(null);
    const { id } = route.params;
    const bullhorns = reactive({ bullhorns: [] });

    async function getItem() {
      loading.value = true;
      const itm = await getDictionaryItem(parseInt(id, 10));
      item.id = itm.id;
      item.term = itm.term;
      item.bullhornIds = itm.bullhorns.map((b) => b.id);
      item.source = itm.source;
      item.creator = itm.createdBy;
      item.description = itm.description;
      [item.postDate, item.postTime] = dateAndTime(itm.createdAt);
      loading.value = false;
    }

    async function saveItem() {
      await saveDictionaryItem({
        id: parseInt(item.id, 10),
        term: item.term,
        description: item.description,
        source: item.source,
        bullhornIds: item.bullhornIds,
      });
    }

    function validateForm() {
      refForm.value.validate((valid) => {
        if (valid) return saveItem();
        return false;
      });
    }

    async function removeItem() {
      try {
        await saveDictionaryItem({
          id: parseInt(item.id, 10),
          delete: true,
        });
      } catch (err) {
        console.log('err', err);
      }
      router.push('/manage-content/directory/dictionary');
    }

    function removeDictionaryItem() {
      removeItemGetConfirm(removeItem);
    }

    onMounted(async () => {
      bullhorns.bullhorns = await getBullhorns();
      await getItem();
    });

    return {
      // data
      refForm,
      rules: {
        term: requiredRule('term'),
        description: requiredRule('description'),
        source: requiredRule('source'),
      },
      item,
      bullhorns,

      // methods
      validateForm,
      removeDictionaryItem,
    };
  },
});
</script>
